import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { SearchIcon, CloseIcon, WarningIcon, CaretDownIcon } from "@/shared/ui/icons";
import {
  cloneElement,
  forwardRef,
  isValidElement,
  type InputHTMLAttributes,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
  useId,
} from "react";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";

interface FieldControlProps {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  required?: boolean;
}

export interface FieldProps {
  label: ReactNode;
  children: ReactElement<FieldControlProps>;
  htmlFor?: string;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
}

/** Visible label, persistent description, control, then an announced error. */
export function Field({
  label,
  children,
  htmlFor,
  description,
  error,
  required,
  className,
}: FieldProps) {
  useLocaleSubscription();
  const generatedId = useId();
  const id = htmlFor ?? children.props.id ?? generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [children.props["aria-describedby"], descriptionId, errorId]
      .filter(Boolean)
      .join(" ") || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : children.props["aria-invalid"],
        required: required ? true : children.props.required,
      })
    : children;

  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <label
        htmlFor={id}
        className="text-[12.5px] font-medium leading-[1.4] tracking-[0.02em] text-ink"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ms-1 font-semibold text-danger">
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> {localeText("interface._required_0002fd23", { defaultValue: "(required)" })}</span> : null}
      </label>
      {description ? (
        <p
          id={descriptionId}
          className="m-0 text-xs leading-[1.55] text-ink-muted"
        >
          {description}
        </p>
      ) : null}
      <div className={description ? "mt-0.5" : undefined}>{control}</div>
      {error ? (
        <p
          id={errorId}
          className="m-0 flex items-start gap-1.5 text-xs leading-[1.5] text-danger-text"
        >
          <WarningIcon className="size-3.5 shrink-0 text-danger" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

const controlClass =
  "min-h-11 w-full min-w-0 studio-field rounded-md px-[14px] py-0 font-sans text-base font-semibold leading-5 text-ink outline-none transition-[background-color,border-color] duration-150 placeholder:text-ink-muted read-only:bg-transparent disabled:bg-canvas-soft disabled:text-ink-faint aria-invalid:bg-danger-subtle aria-invalid:text-danger-text sm:min-h-10 sm:text-sm";

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "prefix"
> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Inside InputGroup, the group supplies this field's fill and border. */
  variant?: "default" | "plain";
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField({ className, prefix, suffix, variant = "default", ...props }, ref) {
    if (!prefix && !suffix) {
      return (
        <input ref={ref} className={cn(controlClass, variant === "plain" && "studio-field-plain", className)} {...props} />
      );
    }
    return (
      <div
        className={cn(
          "flex min-h-11 w-full min-w-0 items-center overflow-hidden studio-field rounded-md transition-[background-color,border-color] duration-150 sm:min-h-10",
          (props["aria-invalid"] === true ||
            props["aria-invalid"] === "true") &&
            "bg-danger-subtle",
          props.disabled && "bg-canvas-soft",
          variant === "plain" && "studio-field-plain",
          className,
        )}
      >
        {prefix ? (
          <span className="flex shrink-0 items-center whitespace-nowrap ps-[14px] text-base font-semibold leading-5 text-ink-muted sm:text-sm">
            {prefix}
          </span>
        ) : null}
        <input
          ref={ref}
          className={cn("min-h-[42px] w-0 min-w-0 flex-1 border-0 bg-transparent px-[14px] py-0 font-sans text-base font-semibold leading-5 text-ink outline-none placeholder:text-ink-muted aria-invalid:text-danger-text disabled:text-ink-faint sm:min-h-[38px] sm:text-sm", prefix && "ps-2", suffix && "pe-2")}
          {...props}
        />
        {suffix ? (
          <span className="flex shrink-0 items-center justify-center whitespace-nowrap pe-[14px] text-base font-semibold leading-5 text-ink-muted sm:text-sm">
            {suffix}
          </span>
        ) : null}
      </div>
    );
  },
);

export interface SearchFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Accessible name for the visually compact search control. */
  label: string;
  /** Called by the optional clear affordance. Controlled inputs should clear their value here. */
  onClear?: () => void;
  clearLabel?: string;
}

/** Pill search used by dense libraries and pickers. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(
    {
      className,
      label,
      onClear,
      clearLabel = "Clear search",
      value,
      disabled,
      ...props
    },
    ref,
  ) {
    const hasValue =
      value !== undefined && value !== null && String(value).length > 0;

    return (
      <div
        className={cn(
          "relative flex min-h-11 w-full min-w-0 items-center gap-2 studio-field rounded-pill ps-3 transition-[background-color,border-color] duration-150 sm:min-h-10",
          onClear ? "pe-12 sm:pe-10" : "pe-3",
          disabled && "bg-canvas-soft text-ink-faint",
          className,
        )}
      >
        <SearchIcon className="size-4 shrink-0 text-ink-muted" />
        <input
          ref={ref}
          type="search"
          {...props}
          aria-label={label}
          value={value}
          disabled={disabled}
          className="min-h-[42px] w-0 min-w-0 flex-1 appearance-none border-0 bg-transparent px-0 py-0 font-sans text-base font-semibold leading-5 text-ink outline-none placeholder:text-ink-muted aria-invalid:text-danger-text disabled:text-ink-faint [&::-webkit-search-cancel-button]:hidden sm:min-h-[38px] sm:text-sm"
        />
        {onClear && hasValue && !disabled ? (
          <IconButton
            aria-label={clearLabel}
            size="sm"
            className="absolute end-0.5 top-1/2 -translate-y-1/2 text-ink-muted sm:end-1"
            onClick={(event) => {
              const input = event.currentTarget.parentElement?.querySelector("input");
              onClear();
              input?.focus();
            }}
          >
            <CloseIcon className="size-4 shrink-0" />
          </IconButton>
        ) : null}
      </div>
    );
  },
);

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: "default" | "plain";
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea({ className, rows = 3, variant = "default", ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          "min-h-[72px] w-full min-w-0 resize-none studio-field rounded-md px-[14px] py-2.5 font-sans text-base font-semibold leading-[1.6] text-ink outline-none transition-[background-color,border-color] duration-150 placeholder:text-ink-muted read-only:bg-transparent disabled:bg-canvas-soft disabled:text-ink-faint aria-invalid:bg-danger-subtle aria-invalid:text-danger-text sm:text-sm",
          variant === "plain" && "studio-field-plain",
          className,
        )}
        {...props}
      />
    );
  },
);

export interface NumberFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  suffix?: ReactNode;
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  function NumberField({ className, suffix, ...props }, ref) {
    return (
      <div
        className={cn(
          "flex min-h-11 min-w-0 items-stretch studio-field rounded-md transition-[background-color,border-color] duration-150 sm:min-h-10",
          (props["aria-invalid"] === true ||
            props["aria-invalid"] === "true") &&
            "bg-danger-subtle",
          props.disabled && "bg-canvas-soft",
          className,
        )}
      >
        <input
          ref={ref}
          type="number"
          className="min-h-[42px] min-w-0 flex-1 border-0 bg-transparent px-[14px] font-sans text-base font-semibold tabular-nums text-ink outline-none aria-invalid:text-danger-text disabled:text-ink-faint sm:min-h-[38px] sm:text-sm"
          {...props}
        />
        {suffix ? (
          <span className="flex items-center px-2.5 text-[12.5px] text-ink-muted">
            {suffix}
          </span>
        ) : null}
      </div>
    );
  },
);


export interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Layout classes for the control and its chevron, such as a compact width. */
  wrapperClassName?: string;
}

/** For native form submission, validation, or platform-owned selection. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  function NativeSelect({ className, wrapperClassName, children, ...props }, ref) {
    const listbox = props.multiple || (props.size !== undefined && props.size > 1);
    return (
      <span dir={props.dir} className={cn("relative inline-flex w-full min-w-0 align-middle", wrapperClassName)}>
        <select
          {...props}
          ref={ref}
          className={cn(controlClass, "peer cursor-pointer py-2", !listbox && "appearance-none pe-11 forced-colors:appearance-auto", className)}
        >
          {children}
        </select>
        {!listbox ? (
          <CaretDownIcon className="pointer-events-none absolute end-[14px] top-1/2 size-4 -translate-y-1/2 text-ink-muted peer-disabled:text-ink-faint forced-colors:hidden" />
        ) : null}
      </span>
    );
  },
);

export interface ColorFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  hideLabel?: boolean;
}

/** Keeps the platform color picker and native keyboard/form behavior. */
export const ColorField = forwardRef<HTMLInputElement, ColorFieldProps>(
  function ColorField({ label, hideLabel, className, id: suppliedID, ...props }, ref) {
    const generatedID = useId();
    const id = suppliedID ?? generatedID;
    return <label htmlFor={id} className="inline-flex min-h-11 items-center gap-2 text-sm text-ink">
      <span className={hideLabel ? "sr-only" : undefined}>{label}</span>
      <input {...props} id={id} ref={ref} type="color" className={cn("studio-color-field studio-field size-11 cursor-pointer appearance-none overflow-hidden rounded-md p-0 outline-none disabled:cursor-not-allowed disabled:opacity-50", className)} />
    </label>;
  },
);


/** One field material around a prompt and its inline toolbar. */
export const InputGroup = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function InputGroup({ className, ...props }, ref) {
    return <div {...props} ref={ref} className={cn("studio-input-group studio-field w-full min-w-0 overflow-hidden rounded-md transition-[border-color] duration-150", className)} />;
  },
);
